import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  InsertDriveFile as FileIcon,
  Signpost as RoadIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import CameraOverlayEditDialog from './CameraOverlayEditDialog';
import {
  CAMERA_OVERLAY_ACCEPT,
  deleteCameraOverlay,
  formatCameraOverlayApiError,
  isSupportedCameraOverlayFile,
  listCameraOverlays,
  updateCameraOverlay,
  uploadCameraOverlayFile,
  type CameraOverlayFileDto,
} from '../../services/cameraOverlayApi';
import { CAMERA_CATEGORY_COLORS, cameraCategoryLabel } from '../../services/cameraOverlayService';
import CameraOverlayImportDialog, {
  type CameraOverlayImportResult,
} from './CameraOverlayImportDialog';
import CameraStreetViewAdmin from './CameraStreetViewAdmin';

const FORMAT_LABELS: Record<string, string> = {
  geojson: 'GeoJSON',
  overpass: 'Overpass',
  rows: 'JSON',
  osm: 'OSM XML',
  gpx: 'GPX',
  kml: 'KML',
  kmz: 'KMZ',
  csv: 'CSV',
};

/**
 * Admin: import shared camera overlays (GeoJSON, Overpass/OSM, GPX, KML/KMZ,
 * CSV, JSON rows) with a preview + field mapper, review past uploads, set
 * source / category, toggle visibility, delete. Visible to all map users once saved.
 */
const CameraOverlaysAdminPanel: React.FC = () => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [files, setFiles] = useState<CameraOverlayFileDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Import queue: one dialog per selected file.
  const [queue, setQueue] = useState<File[]>([]);
  const [queueIndex, setQueueIndex] = useState(0);
  const [imported, setImported] = useState<string[]>([]);
  const [importErrors, setImportErrors] = useState<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await listCameraOverlays();
      setFiles(next);
    } catch (e: unknown) {
      setError(formatCameraOverlayApiError(e, 'Failed to load camera overlays'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const finishQueue = useCallback(
    async (added: string[], errors: string[]) => {
      setQueue([]);
      setQueueIndex(0);
      setImported([]);
      setImportErrors([]);
      if (inputRef.current) inputRef.current.value = '';
      await load();
      if (added.length) {
        setSuccess(
          added.length === 1
            ? `Imported ${added[0]}. All users will see it when the camera layer is on.`
            : `Imported ${added.length} camera overlays (${added.join(', ')}). All users will see them when the camera layer is on.`
        );
      }
      if (errors.length) setError(errors.join('; '));
    },
    [load]
  );

  const handleSelect = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    setError(null);
    setSuccess(null);
    const accepted: File[] = [];
    const rejected: string[] = [];
    Array.from(fileList).forEach((f) => {
      if (isSupportedCameraOverlayFile(f.name)) accepted.push(f);
      else rejected.push(`${f.name}: unsupported type`);
    });
    if (rejected.length) setError(rejected.join('; '));
    if (!accepted.length) {
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setImported([]);
    setImportErrors([]);
    setQueue(accepted);
    setQueueIndex(0);
  };

  const advance = async (added: string[], errors: string[]) => {
    const nextIndex = queueIndex + 1;
    if (nextIndex >= queue.length) {
      await finishQueue(added, errors);
    } else {
      setImported(added);
      setImportErrors(errors);
      setQueueIndex(nextIndex);
    }
  };

  const handleImport = async (result: CameraOverlayImportResult) => {
    const file = queue[queueIndex];
    if (!file) return;
    setBusy(true);
    const added = [...imported];
    const errors = [...importErrors];
    try {
      const saved = await uploadCameraOverlayFile(file, {
        mapping: result.mapping,
        source: result.source,
        category: result.category,
        name: result.name,
      });
      added.push(`${saved.name} (${saved.feature_count})`);
    } catch (e: unknown) {
      errors.push(`${file.name}: ${formatCameraOverlayApiError(e, 'import failed')}`);
    } finally {
      setBusy(false);
    }
    await advance(added, errors);
  };

  const handleSkip = async () => {
    await advance(imported, importErrors);
  };

  const handleCancelQueue = async () => {
    await finishQueue(imported, importErrors);
  };

  const handleToggleActive = async (file: CameraOverlayFileDto) => {
    setError(null);
    setSuccess(null);
    try {
      await updateCameraOverlay(file.id, { active: !file.active });
      await load();
    } catch (e: unknown) {
      setError(formatCameraOverlayApiError(e, 'Failed to update overlay'));
    }
  };

  // Post-upload edit dialog (name / source / category / road names).
  const [editingId, setEditingId] = useState<number | null>(null);
  const editingFile = files.find((f) => f.id === editingId) ?? null;

  const handleDelete = async (file: CameraOverlayFileDto) => {
    if (!window.confirm(`Delete camera overlay "${file.name}" for all users?`)) return;
    setError(null);
    setSuccess(null);
    try {
      await deleteCameraOverlay(file.id);
      setSuccess(`Deleted ${file.name}`);
      await load();
    } catch (e: unknown) {
      setError(formatCameraOverlayApiError(e, 'Failed to delete overlay'));
    }
  };

  const currentFile = queue[queueIndex] ?? null;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Camera Locations
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>Shared map overlays</AlertTitle>
        Import camera locations from <strong>GeoJSON, Overpass / OSM exports, GPX, KML / KMZ, CSV</strong> or
        JSON tables. Each import is previewed first: the detected fields are mapped onto ours (ID, name,
        coordinates, category, status, source) and can be corrected when the schema differs. Every camera
        gets a <strong>source</strong> and a <strong>category</strong> (ALPR, traffic, police, municipal…),
        inferred from the data when not supplied. Saved overlays are stored in the database and shown to
        every user on Pathfinder and COP when the camera layer is enabled. Re-importing the same name
        replaces the previous file. Use the pencil on a past upload to rename it, set its source or
        category, or <strong>derive road names</strong> so cameras are labelled with the nearest
        intersection instead of an id.
      </Alert>

      <CameraStreetViewAdmin />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Button
        variant="contained"
        component="label"
        startIcon={<UploadIcon />}
        disabled={busy || loading || queue.length > 0}
        sx={{ mb: 3 }}
      >
        {busy ? 'Importing…' : 'Import camera file(s)'}
        <input
          ref={inputRef}
          type="file"
          hidden
          multiple
          accept={CAMERA_OVERLAY_ACCEPT}
          onChange={(e) => handleSelect(e.target.files)}
        />
      </Button>

      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Past uploads
      </Typography>
      {loading ? (
        <Typography variant="body2" color="text.secondary">
          Loading…
        </Typography>
      ) : files.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No camera overlay files yet.
        </Typography>
      ) : (
        <List dense sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          {files.map((file) => (
            <ListItem
              key={file.id}
              sx={{ pr: 24 }}
              secondaryAction={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Tooltip title="Edit name, source, category · derive road names">
                    <IconButton
                      edge="end"
                      aria-label={`Edit ${file.name}`}
                      onClick={() => setEditingId(file.id)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, ml: 1 }}>
                    {file.active ? 'Visible' : 'Hidden'}
                  </Typography>
                  <Switch
                    edge="end"
                    checked={file.active}
                    onChange={() => void handleToggleActive(file)}
                    inputProps={{ 'aria-label': `Toggle ${file.name}` }}
                  />
                  <IconButton
                    edge="end"
                    aria-label={`Delete ${file.name}`}
                    color="error"
                    onClick={() => void handleDelete(file)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              }
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <FileIcon color={file.active ? 'primary' : 'disabled'} fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <span>{file.name}</span>
                    {file.source_format && (
                      <Chip size="small" variant="outlined" label={FORMAT_LABELS[file.source_format] || file.source_format} sx={{ height: 20 }} />
                    )}
                    {file.category && (
                      <Chip
                        size="small"
                        label={cameraCategoryLabel(file.category)}
                        sx={{
                          height: 20,
                          color: '#fff',
                          bgcolor: CAMERA_CATEGORY_COLORS[file.category] || CAMERA_CATEGORY_COLORS.other,
                        }}
                      />
                    )}
                    {(file.location_count ?? 0) > 0 && (
                      <Tooltip title="Cameras labelled with a road / intersection name">
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<RoadIcon sx={{ fontSize: 14 }} />}
                          label={
                            file.location_count === file.feature_count
                              ? 'Road names'
                              : `${file.location_count!.toLocaleString()} / ${file.feature_count.toLocaleString()} road names`
                          }
                          sx={{ height: 20 }}
                        />
                      </Tooltip>
                    )}
                  </Stack>
                }
                secondary={`${file.feature_count} camera${file.feature_count === 1 ? '' : 's'}${
                  file.source ? ` · ${file.source}` : ''
                }${file.uploaded_by ? ` · ${file.uploaded_by}` : ''} · ${new Date(file.uploaded_at).toLocaleString()}`}
              />
            </ListItem>
          ))}
        </List>
      )}

      <CameraOverlayEditDialog
        file={editingFile}
        onClose={() => setEditingId(null)}
        onChanged={load}
      />

      <CameraOverlayImportDialog
        file={currentFile}
        queue={queue.length ? { index: queueIndex, total: queue.length } : undefined}
        busy={busy}
        onCancel={() => void handleCancelQueue()}
        onSkip={() => void handleSkip()}
        onImport={(r) => void handleImport(r)}
      />
    </Box>
  );
};

export default CameraOverlaysAdminPanel;
